import { useState, useEffect, useCallback } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';

interface Task {
  content: string;
  timestamp: number;
}

export const useTasks = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOrganisation] = useActiveOrganisation();

  const parseTask = (task: any): Task | null => {
    if (!task) return null;

    // If task is a string, treat it as content with current timestamp
    if (typeof task === 'string') {
      return {
        content: task,
        timestamp: Date.now()
      };
    }

    // If task is an object, try to parse it properly
    if (typeof task === 'object') {
      const content = task.content || task.description || task.text || task.toString();
      let timestamp = task.timestamp || task.date || task.time || Date.now();

      // Ensure timestamp is a number
      if (typeof timestamp === 'string') {
        timestamp = Date.parse(timestamp);
      }
      if (isNaN(timestamp)) {
        timestamp = Date.now();
      }

      return {
        content: content.toString(),
        timestamp
      };
    }

    return null;
  };

  const fetchTasks = useCallback(async () => {
    if (!activeOrganisation?.organizationId) return;

    try {
      const response = await fetch('/api/db/business/metadata/company_tasks', {
        headers: {
          'X-Organization': activeOrganisation.organizationId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const data = await response.json();
      if (data && data.value) {
        try {
          const parsedValue = JSON.parse(data.value);
          if (Array.isArray(parsedValue)) {
            const validTasks = parsedValue
              .map(parseTask)
              .filter((task): task is Task => task !== null);
            setTasks(validTasks);
          } else {
            const task = parseTask(parsedValue);
            setTasks(task ? [task] : []);
          }
        } catch (e) {
          console.error('Error parsing tasks:', e);
          const task = parseTask(data.value);
          setTasks(task ? [task] : []);
        }
      } else {
        setTasks([]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeOrganisation?.organizationId]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchTasks();
  }, [fetchTasks]);

  // Refetch function
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    await fetchTasks();
    setIsRefetching(false);
  }, [fetchTasks]);

  return {
    tasks,
    error,
    refetch,
    isRefetching,
    isLoading,
  };
}; 